import { useRef, useState } from "react";
import { NavLink, Outlet } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError, call, upload } from "../api/client";
import { keys, usePackages, useSystems } from "../api/queries";
import type { PackageAdminView, PackageInstallView, PackageListView } from "../api/types";
import { ConfirmDialog } from "../components/Dialog";
import { text } from "../text";

export function AdminLayout() {
  return (
    <div className="stack">
      <h1>Verwaltung</h1>
      <nav className="tabs" aria-label="Verwaltung">
        <NavLink to="/verwaltung/benutzer">Benutzer</NavLink>
        <NavLink to="/verwaltung/datenpakete">Datenpakete</NavLink>
        <NavLink to="/verwaltung/karten">Karten</NavLink>
        <NavLink to="/verwaltung/anwendungen">Anwendungen</NavLink>
      </nav>
      <Outlet />
    </div>
  );
}

interface Outcome {
  kind: "ok" | "error" | "replace";
  text: string;
  details?: string[];
}

export function PackagesPage() {
  const packages = usePackages();
  const systems = useSystems();
  const client = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [removing, setRemoving] = useState<PackageAdminView | null>(null);

  const refresh = async (list?: PackageListView) => {
    if (list) client.setQueryData(keys.packages, list);
    else await client.invalidateQueries({ queryKey: keys.packages });
    await client.invalidateQueries({ queryKey: keys.systems });
    await client.invalidateQueries({ queryKey: ["character"] });
    await client.invalidateQueries({ queryKey: ["catalog"] });
  };

  /** Runs an action; a conflict on an upload offers to replace the installed package. */
  const run = async (action: () => Promise<void>, uploading = false) => {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && uploading) setOutcome({ kind: "replace", text: e.message });
      else if (e instanceof ApiError) setOutcome({ kind: "error", text: e.message, details: e.messages.map((m) => text(m.text)) });
      else setOutcome({ kind: "error", text: String(e) });
    } finally {
      setBusy(false);
    }
  };

  const send = (replace: boolean) =>
    run(async () => {
      if (!file) return;
      setOutcome(null);
      const result = await upload<PackageInstallView>(`/api/packages${replace ? "?replace=true" : ""}`, file);
      const title = text(result.package.title);
      setOutcome({
        kind: "ok",
        text: result.replacedVersion
          ? `„${title}“ ersetzt: Version ${result.replacedVersion} → ${result.package.version}.`
          : `„${title}“ (Version ${result.package.version}) installiert.`,
      });
      setFile(null);
      if (input.current) input.current.value = "";
      await refresh();
    }, true);

  const remove = async (p: PackageAdminView) => {
    setOutcome(null);
    await call(api.DELETE("/api/packages/{id}", { params: { path: { id: p.id } } }));
    setOutcome({ kind: "ok", text: `„${text(p.title)}“ entfernt.` });
    await refresh();
  };

  const reload = () =>
    void run(async () => {
      setOutcome(null);
      const list = await call<PackageListView>(api.POST("/api/packages/reload"));
      setOutcome({ kind: "ok", text: "Pakete neu eingelesen." });
      await refresh(list);
    });

  const list = packages.data?.packages ?? [];
  const bySystem = [...new Set(list.map((p) => p.system))];
  const systemName = (id: string) => text(systems.data?.find((s) => s.id === id)?.name) || `${id} (System nicht eingeschaltet)`;
  const maxMb = packages.data ? Math.round(packages.data.maxUploadBytes / (1024 * 1024)) : null;

  return (
    <div className="stack">
      <form
        className="card stack"
        onSubmit={(e) => {
          e.preventDefault();
          void send(false);
        }}
      >
        <h3>Paket hochladen</h3>
        <p className="muted small">
          ZIP-Datei mit <code>package.json</code> und den JSON-Dateien des Pakets{maxMb ? `, höchstens ${maxMb} MB` : ""}. Das Paket wird vor dem Speichern geprüft und gilt danach ohne Neustart.
          Pakete mit „Standard“ gelten für alle Charaktere, die sie nicht abgewählt haben.
        </p>
        <label className="field">
          <span>ZIP-Datei</span>
          <input
            ref={input}
            type="file"
            accept=".zip,application/zip"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setOutcome(null);
            }}
          />
        </label>
        <div className="actions">
          <button type="submit" className="button primary" disabled={!file || busy}>
            Hochladen
          </button>
          <button type="button" className="button secondary" disabled={busy} onClick={reload}>
            Neu einlesen
          </button>
        </div>
        {outcome && (
          <div className={outcome.kind === "ok" ? "notice" : "notice error"} role="status">
            <p>{outcome.text}</p>
            {outcome.details && outcome.details.length > 0 && (
              <ul>
                {outcome.details.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            )}
            {outcome.kind === "replace" && (
              <button type="button" className="button primary" disabled={busy} onClick={() => void send(true)}>
                Ersetzen
              </button>
            )}
          </div>
        )}
      </form>

      {packages.isLoading && <p className="muted">Lade …</p>}
      {bySystem.map((system) => (
        <section key={system} className="card stack">
          <h3>{systemName(system)}</h3>
          <ul className="package-list">
            {list
              .filter((p) => p.system === system)
              .map((p) => (
                <li key={p.id} data-package={p.id}>
                  <div className="package-head">
                    <span>
                      <strong>{text(p.title)}</strong> <span className="muted">{p.version}</span>
                      {p.kind === "overlay" && <span className="badge">Overlay</span>}
                      {p.default && <span className="badge">Standard</span>}
                      <span className="badge">{p.origin === "local" ? "hochgeladen" : "mitgeliefert"}</span>
                    </span>
                    {p.origin === "local" && (
                      <button type="button" className="link danger" disabled={busy} onClick={() => setRemoving(p)}>
                        Entfernen
                      </button>
                    )}
                  </div>
                  <span className="muted small">
                    {p.id}
                    {p.requires.length > 0 && ` · braucht ${p.requires.join(", ")}`}
                    {p.conflicts.length > 0 && ` · verträgt sich nicht mit ${p.conflicts.join(", ")}`}
                  </span>
                  {p.description && <span className="muted small">{text(p.description)}</span>}
                </li>
              ))}
          </ul>
        </section>
      ))}

      {(packages.data?.problems.length ?? 0) > 0 && (
        <section className="card danger stack">
          <h3>Nicht geladene Ordner</h3>
          <ul className="package-list">
            {packages.data!.problems.map((p) => (
              <li key={p.directory}>
                <strong>{p.directory}</strong>: {p.text}
              </li>
            ))}
          </ul>
        </section>
      )}

      {removing && (
        <ConfirmDialog title="Datenpaket entfernen" action="Entfernen" danger onClose={() => setRemoving(null)} onConfirm={() => remove(removing)}>
          <p>„{text(removing.title)}“ wird entfernt. Charaktere, die es nutzen, rechnen danach ohne das Paket.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}
